import videojs from "video.js";
import "video.js/dist/video-js/video-js.css";
import { WebVTT, VTTCue, VTTRegion } from "vtt.js";
import type { VideoJsPlayer } from "@/types/videojs-player";

declare global {
  interface Window {
    videojs: typeof videojs;
    WebVTT?: unknown;
    vttjs?: unknown;
    VTTCue?: unknown;
    VTTRegion?: unknown;
  }
}

if (typeof window !== "undefined") {
  window.videojs = videojs;

  // Video.js 4.x وقتی native text track ندارد، تلاش می‌کند vtt.js را با مسیر
  // نسبیِ اشتباه ("../node_modules/vtt.js/dist/vtt.js") تزریق کند و بلافاصله
  // window.WebVTT را روی true می‌گذارد. در نتیجه parseCues چون WebVTT تابع
  // نیست تا ابد منتظر می‌ماند و زیرنویس هرگز نمایش داده نمی‌شود. اینجا نسخهٔ
  // باندل‌شدهٔ vtt.js را مستقیماً روی window می‌گذاریم تا این مسیر شکسته دور زده شود.
  if (typeof window.WebVTT !== "function" && typeof WebVTT === "function") {
    window.WebVTT = WebVTT;
    window.vttjs = { VTTCue: VTTCue, VTTRegion: VTTRegion };
    window.VTTCue = VTTCue;
    window.VTTRegion = VTTRegion;
  }
}

export default videojs;

export function patchVideoJsPlayer(player: VideoJsPlayer): VideoJsPlayer {
  if (typeof player.isDisposed === "function") {
    return player;
  }

  var disposed = false;
  var originalDispose = player.dispose.bind(player);

  player.dispose = function () {
    // disposed را بعد از اتمام dispose ست می‌کنیم تا خودِ dispose داخلی
    // Video.js (که trigger('dispose') و off را صدا می‌زند) به‌درستی اجرا شود.
    try {
      originalDispose();
    } finally {
      disposed = true;
    }
  };

  player.isDisposed = function () {
    return disposed;
  };

  // پس از dispose، المان داخلی پلیر (el_) برابر null می‌شود. اگر cleanupهای
  // افکت‌های React بعد از dispose روی همان پلیر on/off/one/trigger صدا بزنند،
  // Video.js 4 هنگام خواندن expando (vdata...) روی null خطا می‌دهد. این
  // wrapperها چنین فراخوانی‌هایی را بعد از dispose بی‌اثر و ایمن می‌کنند.
  var methodsToGuard: Array<"on" | "off" | "one" | "trigger"> = [
    "on",
    "off",
    "one",
    "trigger",
  ];

  methodsToGuard.forEach(function (name) {
    var original = (player as unknown as Record<string, unknown>)[name];
    if (typeof original !== "function") {
      return;
    }
    var bound = (original as (...a: unknown[]) => unknown).bind(player);
    (player as unknown as Record<string, unknown>)[name] = function () {
      if (disposed) {
        return undefined;
      }
      try {
        return bound.apply(
          player,
          Array.prototype.slice.call(arguments)
        );
      } catch (e) {
        return undefined;
      }
    };
  });

  return player;
}

export function isPlayerDisposed(player: VideoJsPlayer | null): boolean {
  if (!player) {
    return true;
  }
  if (typeof player.isDisposed === "function") {
    return player.isDisposed();
  }
  return false;
}

export function safePlay(player: VideoJsPlayer): void {
  if (isPlayerDisposed(player)) {
    return;
  }

  var result = player.play();
  if (result && typeof (result as Promise<void>).catch === "function") {
    (result as Promise<void>).catch(function () {
      /* autoplay blocked */
    });
  }
}

/** Video.js 4 — وضعیت پایان پخش (techGet('ended') روی HTML5). */
export function isPlayerEnded(player: VideoJsPlayer): boolean {
  if (isPlayerDisposed(player)) {
    return false;
  }

  try {
    return player.ended();
  } catch (e) {
    var duration = player.duration();
    var currentTime = player.currentTime();
    if (
      typeof duration === "number" &&
      isFinite(duration) &&
      duration > 0 &&
      typeof currentTime === "number" &&
      isFinite(currentTime)
    ) {
      return currentTime >= duration - 0.25;
    }
    return false;
  }
}
